/**
 * Crew List Page
 *
 * Admin page for viewing and managing all crew members, including QR
 * signup codes for self-serve crew joining (career fairs, posters).
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CrewList } from '../../../components/crew';
import type { MemberStateFilter } from '../../../api/users';
import { CrewCSVImportModal } from '../../../components/crew/CSVUpload';
import QrSignupSection, { GenerateQrButton } from '../../../components/crew/QrSignupSection';

export default function CrewListPage() {
  const [showImportModal, setShowImportModal] = useState(false);
  const [showQrCreate, setShowQrCreate] = useState(false);
  // Bumped after a successful CSV import to trigger the crew list to refetch
  // without forcing a full page reload (which lost filters/scroll state).
  const [refetchToken, setRefetchToken] = useState(0);

  // ?state=ARCHIVED is the deep link the auto-archive notice email sends
  // admins to. Anything unrecognised falls back to the active roster.
  const [searchParams] = useSearchParams();
  const requestedState = searchParams.get('state');
  const initialState: MemberStateFilter | undefined =
    requestedState === 'ARCHIVED' || requestedState === 'DEACTIVATED' || requestedState === 'ALL'
      ? requestedState
      : undefined;

  return (
    <div>
      {/* Page header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Crew Management</h1>
          <p className="text-gray-600 mt-1">
            Manage your crew members, roles, and access.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <GenerateQrButton onClick={() => setShowQrCreate(true)} />
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rack"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Import CSV
          </button>
          <Link
            to="/admin/crew/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rack"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Invite Crew Member
          </Link>
        </div>
      </div>

      {/* Active QR signup codes + create/view/revoke modals */}
      <QrSignupSection createOpen={showQrCreate} onCloseCreate={() => setShowQrCreate(false)} />

      {/* Crew list. Bulk actions are on here and nowhere else — this is the
          only place an admin has a multi-person selection to act on. */}
      <CrewList
        refetchToken={refetchToken}
        enableBulkActions
        initialFilters={initialState ? { state: initialState } : undefined}
      />

      {/* CSV Import Modal */}
      <CrewCSVImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => setRefetchToken((t) => t + 1)}
      />
    </div>
  );
}
